import { Typography, Button, Box, Grid, MenuItem, Link } from '@material-ui/core'
import React, { Dispatch, useState } from 'react'
import { SitesClient, SiteRequestDTO, SiteResponseDTO, Status, Active, Archived, OrganizationsClient, OrganizationResponseDTO } from '../generated/ApiClient'
import { Formik, Form, Field, FieldProps } from 'formik'
import { Select, TextField } from 'formik-material-ui'
import * as Yup from 'yup'
import { useDispatch } from "react-redux"
import { actionCreators as snackbar } from '../store/AlertSnackbarStore'
import { DateTime } from 'luxon'
import { utcToLocal } from '../common/Shared'
import { Link as RouterLink } from 'react-router-dom'
import { MultiLineTextField } from '../common/SharedUI'
import { RouteComponentProps, useHistory } from 'react-router'
import { History } from 'history'
import CenteredLoader from '../common/CenteredLoader'
import { OrgSiteModemEditor, useIsUserInRoles } from '../hooks/useIsUserInRoles'

const SiteFormSchema = Yup.object().shape({
    name: Yup.string().max(100).required(),
    status: Yup.string().oneOf(['Active', 'Archived']).required(),
    address: Yup.string().max(125).required(),
    city: Yup.string().max(100).required(),
    state: Yup.string().max(50).required(),
    zip: Yup.string().max(15).required(),
    country: Yup.string().max(40).required(),
    firstName: Yup.string().max(50).required(),
    lastName: Yup.string().max(50).required(),
    phoneNumber: Yup.string().max(20).required(),
    email: Yup.string().max(50).required(),
    organizationId: Yup.string().max(50).required(),
    agileProjectContainerNumber: Yup.string().max(50),
    salesForceProjectKey: Yup.string().max(50),
    notes: Yup.string().max(500),
})

type SiteFormProperties = Yup.InferType<typeof SiteFormSchema>

function toRequestDTO(orgId: string, formData: SiteFormProperties) {
    const dtoData: SiteRequestDTO = {
        organizationId: orgId,
        name: formData.name,
        address: formData.address,
        agileProjectContainerNumber: formData.agileProjectContainerNumber,
        city: formData.city,
        country: formData.country,
        notes: formData.notes,
        salesForceProjectKey: formData.salesForceProjectKey,
        stateProv: formData.state,
        zip: formData.zip,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        status: { Case: formData.status } as Status
    };
    return dtoData;
}

type Props = {
    siteData: SiteResponseDTO
    siteId: string
} | RouteComponentProps<{
    siteId?: string | undefined
    organizationId?: string | undefined
}>

async function formSubmitCallback(externalId: string | undefined, orgId: string, siteValues: SiteFormProperties, dispatch: Dispatch<any>, history: History<any>) {
    const requestData: SiteRequestDTO = toRequestDTO(orgId, siteValues);

    try {
        if (externalId) {
            await new SitesClient().update3(externalId, requestData);
        } else {
            const newSite = await new SitesClient().create2(requestData);
            if (newSite.externalId) {
                history.goBack();
            }
        }
        dispatch(snackbar.addMessage({ title: "Successfully updated", message: `Site details were saved`, alertType: 'success' }))
    }
    catch (error) {
        dispatch(snackbar.addError(error, undefined, 'Unable to save site'));
    }
}

async function deleteSite(siteId: string, organizationId: string, dispatch: Dispatch<any>, history: History<any>) {
    try {
        await new SitesClient().delete2(siteId);
        dispatch(snackbar.addMessage({ title: "Successfully deleted", message: `Site was deleted`, alertType: 'success' }))
        history.push(`/organizations/${organizationId}`);
    }
    catch (error) {
        dispatch(snackbar.addError(error, undefined, 'Error deleting site'));
    }
}

export function SiteDetailsForm(props: Props) {
    const history = useHistory()
    const [organizations, setOrganizations] = React.useState<OrganizationResponseDTO[]>([])
    const [formOrg, setFormOrg] = React.useState<OrganizationResponseDTO>()
    const siteId = 'siteId' in props ? props.siteId : props.match.params.siteId
    const site = 'siteData' in props ? props.siteData : undefined
    const organizationId = 'siteData' in props ? props.siteData.organizationId : props.match.params.organizationId
    const dispatch = useDispatch()
    const canEditSites = useIsUserInRoles(OrgSiteModemEditor)

    React.useEffect(() => {
        const fetchData = async () => {
            const organizationsLocal = await new OrganizationsClient().getMany();
            setOrganizations(organizationsLocal);
            if (organizationId)
                setFormOrg(organizationsLocal.find(s => s.externalId === organizationId))
            else
                setFormOrg(organizationsLocal[0])
        }
        fetchData();
    }, [siteId]);

    function changeHandler(event: any) {
        if (event.target.name == 'organizationId') {
            const orgId: string = event.target.value;
            const newOrganization = organizations.find(o => o.externalId === orgId)
            setFormOrg(newOrganization);
        }
    }

    if (formOrg)
        return (
            <Formik initialValues={{
                name: site?.name || "",
                status: site?.status?.Case || 'Active',
                address: site?.address || "",
                city: site?.city || "",
                state: site?.stateProv || "",
                zip: site?.zip || "",
                country: site?.country || "",
                firstName: site?.firstName || "",
                lastName: site?.lastName || "",
                phoneNumber: site?.phoneNumber || "",
                email: site?.email || "",
                organizationId: formOrg.externalId,
                agileProjectContainerNumber: site?.agileProjectContainerNumber || "",
                salesForceProjectKey: site?.salesForceProjectKey || "",
                notes: site?.notes || "",
            } as SiteFormProperties}
                validationSchema={SiteFormSchema}
                onSubmit={async (values, _) => formSubmitCallback(siteId, formOrg.externalId, values, dispatch, history)}>
                {/* supply a function to formik so that we can access these props*/}
                {({ submitForm, isSubmitting, touched }) => (
                    <Grid container>
                        <Grid item sm={10}>
                            <Form translate='yes'>
                                <Box my={1.5}>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Name:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {/* pass in a material ui component to the Formik <Field /> component */}
                                            {canEditSites ?
                                                <Field component={TextField} name='name' type='text' />
                                                : <Typography>{site?.name}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Status:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field as='select' component={Select} name='status' >
                                                    <MenuItem value='Active'>Active</MenuItem>
                                                    <MenuItem value='Archived'>Archived</MenuItem>
                                                </Field>
                                                : <Typography>{site?.status?.Case}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Address:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field component={TextField} name='address' type='text' />
                                                : <Typography>{site?.address}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>City:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field component={TextField} name='city' type='text' />
                                                : <Typography>{site?.city}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>State:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field component={TextField} name='state' type='text' />
                                                : <Typography>{site?.stateProv}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Zip Code:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field component={TextField} name='zip' type='text' />
                                                : <Typography>{site?.zip}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Country:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field component={TextField} name='country' type='text' />
                                                : <Typography>{site?.country}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Organization:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {organizations.length > 0 && //mui emits a warning if the selected value is outside the range, like when the orgs haven't loaded yet https://github.com/mui-org/material-ui/issues/18494
                                                (siteId ?
                                                    <Link component={RouterLink} to={`/organizations/${organizationId}`} >
                                                        {formOrg.name}
                                                    </Link>
                                                    : <Field
                                                        as='select'
                                                        component={Select}
                                                        name='organizationId'
                                                        onChange={(event: any) => changeHandler(event)}
                                                        value={formOrg.externalId}
                                                    >
                                                        {organizations.map((org) => <MenuItem value={org.externalId} key={org.externalId}>{org.name}</MenuItem>)}
                                                    </Field>)}
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Box my={1.5}>
                                    <Typography paragraph><b>Related Links</b> </Typography>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Agile Project Container Number:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field component={TextField} name='agileProjectContainerNumber' type='text' />
                                                : <Typography>{site?.agileProjectContainerNumber}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Salesforce Project Key:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field component={TextField} name='salesForceProjectKey' type='text' />
                                                : <Typography>{site?.salesForceProjectKey}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Box my={1.5}>
                                    <Typography paragraph><b>Person of Contact</b> </Typography>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>First Name:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field component={TextField} name='firstName' type='text' />
                                                : <Typography>{site?.firstName}</Typography>
                                            }
                                        </Grid>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Last Name:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field component={TextField} name='lastName' type='text' />
                                                : <Typography>{site?.lastName}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid container alignItems='center' spacing={1}>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Phone Number:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field component={TextField} name='phoneNumber' type='text' />
                                                : <Typography>{site?.phoneNumber}</Typography>
                                            }
                                        </Grid>
                                        <Grid item>
                                            <Typography paragraph display='inline'><b>Email:</b></Typography>
                                        </Grid>
                                        <Grid item>
                                            {canEditSites ?
                                                <Field component={TextField} name='email' type='text' />
                                                : <Typography>{site?.email}</Typography>
                                            }
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Grid container spacing={1}>
                                    <Grid item>
                                        <Typography paragraph display='inline'><b>Notes:</b></Typography>
                                    </Grid>
                                    <Grid item>
                                        <Field component={MultiLineTextField} name='notes' type='text' />
                                    </Grid>
                                </Grid>
                                {site &&
                                    <>
                                        <Grid container alignItems='center' spacing={1}>
                                            <Grid item>
                                                <Typography paragraph display='inline'><b>Created Date:</b></Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography paragraph display='inline'>{utcToLocal(site.created).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}</Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid container alignItems='center' spacing={1}>
                                            <Grid item>
                                                <Typography paragraph display='inline'><b>Last Updated:</b></Typography>
                                            </Grid>
                                            <Grid item>
                                                <Typography paragraph display='inline'>{utcToLocal(site.lastUpdated).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}</Typography>
                                            </Grid>
                                        </Grid>
                                    </>}
                            </Form>
                        </Grid>
                        {canEditSites &&
                            <Grid container item sm={2} direction='column' spacing={2} alignItems='center'>
                                <Grid item>
                                    <Button variant='contained' color='primary' size='medium' disabled={isSubmitting} onClick={submitForm}>Save Changes</Button>
                                </Grid>
                                {site &&
                                    <Grid item>
                                        <Button variant='contained' color='primary' size='medium' onClick={d => deleteSite(site.externalId, site.organizationId, dispatch, history)}>Delete</Button>
                                    </Grid>}
                            </Grid>}
                    </Grid>
                )}
            </Formik>
        );
    else
        return <CenteredLoader />
}
